/**
 * The REALISTIC INSTRUMENTS toggle (manual: "When turned on, the only
 * instruments seen from the cockpit will be the ones that existed on the
 * original WWI aircraft. When turned off, all possible instruments will be
 * displayed", `docs/game/manual/04-gameplay.md:77`).
 *
 * `packages/engine`'s `RealismSettings` deliberately excludes this one —
 * `tiers.ts`'s own doc comment calls it out as "presentation... belongs to
 * the UI and campaign lanes" — so it lives here instead, persisted the same
 * way `audio/settings.ts` persists volume: a small `SettingsStore`
 * round-trip, storage-agnostic so it's testable without a real
 * `localStorage`.
 *
 * Default is **off** (show every instrument, and keep the existing digital
 * HUD gauges visible) rather than the more "authentic" on — a port-side
 * choice for backward compatibility: the digital HUD predates this lane and
 * defaulting the toggle on would silently start hiding gauges callers may
 * already depend on seeing. `docs/game/ui-menus.md`'s Realism Panel section
 * has no recovered default for this toggle either, so there's no fidelity
 * argument either way.
 */

import type { SettingsStore } from "../audio/settings.ts";

export const COCKPIT_SETTINGS_STORAGE_KEY = "red-opus/cockpit-settings";

export interface CockpitSettings {
  readonly realisticInstruments: boolean;
}

export const DEFAULT_COCKPIT_SETTINGS: CockpitSettings = { realisticInstruments: false };

/** Reads whatever survives a `JSON.parse` of untrusted stored text, falling back per-field to the default. */
export function normalizeCockpitSettings(candidate: unknown): CockpitSettings {
  if (typeof candidate !== "object" || candidate === null) return DEFAULT_COCKPIT_SETTINGS;
  const raw = candidate as Partial<Record<keyof CockpitSettings, unknown>>;
  return { realisticInstruments: raw.realisticInstruments === true };
}

export function serializeCockpitSettings(settings: CockpitSettings): string {
  return JSON.stringify(settings);
}

/** Defaults on missing key, corrupt JSON, or a non-object payload — never throws. */
export function loadCockpitSettings(store: SettingsStore): CockpitSettings {
  const raw = store.getItem(COCKPIT_SETTINGS_STORAGE_KEY);
  if (raw === null) return DEFAULT_COCKPIT_SETTINGS;
  try {
    return normalizeCockpitSettings(JSON.parse(raw));
  } catch {
    return DEFAULT_COCKPIT_SETTINGS;
  }
}

export function saveCockpitSettings(store: SettingsStore, settings: CockpitSettings): void {
  store.setItem(COCKPIT_SETTINGS_STORAGE_KEY, serializeCockpitSettings(settings));
}
